import React, { useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'

import { Navigate, useNavigate } from '@lib/react-router-dom'

import { useSignup } from '@context/SignupContext'

import { AccountType } from '@model/AccountType'
import { isLeadSourceMobileRegistration } from '@model/LeadDto'

import { useLeadOTPVerification } from '@hooks/useLeadOTPVerification'

import MobileAppRedirectSuccessPage from './MobileAppRedirectSuccessPage'
import OTPVerificationPage from './OTPVerificationPage'
import { VerifyEmailInfoPage } from './verifyEmailInfoPage'

export const LeadCreationPage: React.FC = () => {
  const { signupData } = useSignup()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const accountTypeId = searchParams.get('accountTypeId') as AccountType | null
  const emailFromParams = searchParams.get('email')?.trim() || ''
  const error = searchParams.get('error')

  const leadId = signupData.lead?.id ?? signupData.leadResponse?.leadId ?? null
  const email = signupData.values?.email?.trim() || signupData.lead?.email || emailFromParams
  const totalSteps = signupData.totalSteps.personalDetails

  const { resolvedEmail, handleResend, handleVerifyCode, handleContinue } = useLeadOTPVerification({
    leadId,
    email,
    token: null,
  })

  const hasOtpContext = Boolean(leadId || email)
  const isMobileLead = isLeadSourceMobileRegistration(signupData.lead?.leadSource)

  const shouldShowVerifyEmailInfoPage = error === 'lead_verification_error'

  useEffect(() => {
    if (shouldShowVerifyEmailInfoPage) {
      return
    }

    if (!signupData.lead?.emailVerified) {
      return
    }

    if (isMobileLead) {
      return
    }

    if (signupData.lead.token) {
      navigate(
        `/sign-up/personal-details/info/?hash=${encodeURIComponent(signupData.lead.token)}`,
        {
          replace: true,
          state: { leadId: signupData.lead.id },
        }
      )
      return
    }

    navigate('/sign-up/personal-details/info/', {
      replace: true,
      state: { leadId: signupData.lead.id },
    })
  }, [isMobileLead, navigate, signupData.lead, shouldShowVerifyEmailInfoPage])

  if (!hasOtpContext) {
    return <Navigate to='/login' replace />
  }

  if (isMobileLead) {
    return <MobileAppRedirectSuccessPage lead={signupData.lead} />
  }

  if (shouldShowVerifyEmailInfoPage) {
    return (
      <VerifyEmailInfoPage
        accountTypeId={Number(accountTypeId ?? signupData.values.typeId)}
        email={resolvedEmail}
        totalSteps={totalSteps}
        step={3}
        onResend={handleResend}
      />
    )
  }

  return (
    <OTPVerificationPage
      accountTypeId={Number(accountTypeId ?? signupData.values.typeId)}
      email={resolvedEmail}
      totalSteps={totalSteps}
      step={3}
      onResend={handleResend}
      onVerifyCode={handleVerifyCode}
      onNext={handleContinue}
    />
  )
}
